<template>
  <lazy-widgets-newsletter-popup :_email="email" v-if="showModal"></lazy-widgets-newsletter-popup>
  <div>
    <footer class="bg-gray-50 print:hidden">
      <!-- Newsletter Section -->
      <div class="bg-gray-100">
        <div class="container mx-auto px-4">
          <section class="py-6 border-b border-gray-200">
            <div class="flex flex-col lg:flex-row items-center justify-between gap-4">
              <div class="lg:w-1/2">
                <div class="text-center lg:text-left">
                  <h4 class="text-lg font-semibold text-gray-800 mb-1">{{ $t("know_it") }}</h4>
                  <p class="text-gray-600 text-sm">{{ $t("never_miss") }}</p>
                </div>
              </div>
              <div class="lg:w-1/2">
                <form class="flex flex-col sm:flex-row gap-2 items-center justify-center lg:justify-end">
                  <div class="flex-1 w-full sm:w-auto">
                    <input 
                      type="text" 
                      v-model="email" 
                      class="w-full px-4 py-2 rounded-lg border border-gray-300 transition-all hover:border-[var(--color-primary)] hover:ring-2 hover:ring-[rgba(8,184,81,0.2)] focus:outline-none focus:ring-2 focus:ring-[rgba(8,184,81,0.2)] focus:border-[var(--color-primary)]"
                      name="EMAIL" 
                      id="mce-EMAIL"
                      :placeholder="$t('enter_email')" 
                      required="required" 
                    />
                  </div>
                  <button 
                    @click.prevent="showNewsLetter" 
                    type="submit" 
                    class="px-6 py-2 bg-[#009d66] text-white font-medium rounded-lg hover:bg-[#009d66] transition-colors whitespace-nowrap"
                  >
                    {{ $t('subscribe') }}
                  </button>
                </form>
              </div>
            </div>
          </section>
        </div>
      </div>
      
      <!-- Main Footer Content -->
      <section class="py-10 bg-gray-50">
        <div class="container mx-auto px-4">
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <!-- Logo & Description -->
            <div class="lg:col-span-1">
              <div class="mb-4">
                <img class="h-[120px] w-auto" src="~~/assets/cld-logo.svg" alt="logo" width="181" height="120" />
              </div>
              <p class="text-gray-600 text-sm leading-relaxed mb-4" v-html="$t('cld_desc1', { newline: '<br>' })"></p>
              
              <!-- Social Icons -->
              <div class="flex gap-3">
                <a href="https://www.facebook.com/cldgroupe" target="_blank" aria-label="Facebook"
                   class="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 hover:bg-[#1877F2] hover:text-white transition-colors">
                  <i class="fa fa-facebook" aria-hidden="true"></i>
                </a>
                <a href="https://www.youtube.com/@CLD-Group" target="_blank" aria-label="Youtube"
                   class="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 hover:bg-[#FF0000] hover:text-white transition-colors">
                  <i class="fa fa-youtube" aria-hidden="true"></i>
                </a>
                <a href="https://www.instagram.com/cld_group/" target="_blank" aria-label="Instagram"
                   class="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 hover:bg-gradient-to-r hover:from-[#FD5949] hover:via-[#D6249F] hover:via-[#009d66] hover:to-[#285AEB]  hover:text-white transition-colors">
                  <i class="fa fa-instagram" aria-hidden="true"></i>
                </a>
                <a href="https://www.linkedin.com/company/cldgroup/" target="_blank" aria-label="LinkedIn"
                   class="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 hover:bg-[#0A66C2] hover:text-white transition-colors">
                  <i class="fa fa-linkedin" aria-hidden="true"></i>
                </a>
              </div>
            </div>
            
            <!-- Account Links (Authenticated users only) -->
            <div class="lg:col-span-1 lg:pl-8" v-if="isAuthenticated">
              <h4 class="text-lg font-bold uppercase tracking-wider text-gray-800 mb-4 pb-2 border-b border-gray-200">
                {{ $t("account_links") }}
              </h4>
              <ul class="space-y-2">
                <li>
                  <NuxtLinkLocale to="/order/history" class="text-gray-600 hover:text-[#009d66] transition-colors text-sm font-medium">
                    {{ $t(`menu.order_history`) }}
                  </NuxtLinkLocale>
                </li>
                <li>
                  <NuxtLinkLocale to="/order/cartmanagement" class="text-gray-600 hover:text-[#009d66] transition-colors text-sm font-medium">
                    {{ $t(`menu.cart_management`) }}
                  </NuxtLinkLocale>
                </li>
                <li>
                  <NuxtLinkLocale to="/order/orderedProducts" class="text-gray-600 hover:text-[#009d66] transition-colors text-sm font-medium">
                    {{ $t(`menu.back_orders`) }}
                  </NuxtLinkLocale>
                </li>
                <li>
                  <NuxtLinkLocale to="/order/deliverables" class="text-gray-600 hover:text-[#009d66] transition-colors text-sm font-medium">
                    {{ $t(`menu.deliverable_orders`) }}
                  </NuxtLinkLocale>
                </li>
                <li>
                  <NuxtLinkLocale to="/order/unavailableDeliveries" class="text-gray-600 hover:text-[#009d66] transition-colors text-sm font-medium">
                    {{ $t(`menu.awaiting_delivery`) }}
                  </NuxtLinkLocale>
                </li>
                <li>
                  <a target="_blank" :href="tAndCPdf[selectedLanguage]" class="text-gray-600 hover:text-[#009d66] transition-colors text-sm font-medium flex items-center gap-1">
                    <i class="fa fa-file-text-o mr-1"></i>
                    {{ $t(`general_terms_and_conditions`, "General terms and conditions") }}
                  </a>
                </li>
              </ul>
            </div>

             <!-- B2C Contracts Column -->
                <div class="lg:col-span-1">
                <DropshippingPartners />
                </div>

            <!-- Store Information -->
            <div class="lg:col-span-1">
              <h4 class="text-lg font-bold uppercase tracking-wider text-gray-800 mb-4 pb-2 border-b border-gray-200">
                {{ $t("store_information") }}
              </h4>
              <ul class="space-y-2 mb-3">
                <li>
                  <NuxtLinkLocale to="/faq" class="text-gray-600 hover:text-primary transition-colors text-sm font-medium">
                    {{ $t('faq.title') }}
                  </NuxtLinkLocale>
                </li>
                <li>
                  <NuxtLinkLocale to="/story" class="text-gray-600 hover:text-primary transition-colors text-sm font-medium">
                    {{ $t('our_story') }}
                  </NuxtLinkLocale>
                </li>
              </ul>
              <LegalDocs />
            </div>
          </div>
        </div>
      </section>
      
      <!-- Copyright -->
      <div class="bg-gray-200 py-4">
        <div class="container mx-auto px-4">
          <div class="flex flex-col sm:flex-row items-center justify-between">
            <div class="text-gray-600 text-sm">
              <i class="fa fa-copyright" aria-hidden="true"></i> 2023-24 CLD Distribution
            </div>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { useFilterStore } from '~/store/FilterStore';
import { useUserStore } from '~/store/UserStore';
import { useUtilityStore } from '~/store/UtililityStore';
import DropshippingPartners from '../DropshippingPartners.vue';

const email = ref('')
const filterStore = useFilterStore()
const showModal = computed(() => filterStore.showNewsLetterModal)
const showNewsLetter = () => {
  filterStore.setShowNewsLetterModal(true)
}
const userStore = useUserStore();
const isAuthenticated = computed(() => userStore.isAuthenticated);
const utilityStore = useUtilityStore()
const selectedLanguage = computed(() => utilityStore.selectedLanguage)
const tAndCPdf = {
  "de_DE": '/pdf/terms/General conditions of sales for CLD distribution SA-EN-2026.pdf',
  "es_ES": '/pdf/terms/General conditions of sales for CLD distribution SA-EN-2026.pdf',
  "sv_SE": '/pdf/terms/General conditions of sales for CLD distribution SA-EN-2026.pdf',
  'en_GB': '/pdf/terms/General conditions of sales for CLD distribution SA-EN-2026.pdf',
  "nl_BE": '/pdf/terms/Algemene verkoopvoorwaarden van CLD distribution SA - NL-2026.pdf',
  "fr_BE": "/pdf/terms/Conditions générales de vente CLD distribution SA - FR.2026.pdf"
}

</script>
